import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    FaCheck,
    FaClipboardList,
    FaHistory,
    FaMotorcycle,
    FaShoppingBag,
    FaUtensils,
    FaCoffee,
    FaSun,
    FaCookie,
    FaMoon,
    FaFileInvoiceDollar,
} from 'react-icons/fa';
import { MEAL_TYPE_OPTIONS } from './mealTypesDropdown';

export const ORDER_VIEW_OPTIONS = [
    {
        value: 'orders',
        label: 'Active Orders',
        hint: 'Live orders in progress',
        Icon: FaClipboardList,
        color: '#1d4ed8',
        bg: '#dbeafe',
    },
    {
        value: 'orderHistory',
        label: 'Order History',
        hint: 'Completed and past orders',
        Icon: FaHistory,
        color: '#475569',
        bg: '#e2e8f0',
    },
];

/** Catering orders tab: active orders, customer quote requests, and history. */
export const CATERING_VIEW_OPTIONS = [
    ORDER_VIEW_OPTIONS[0],
    {
        value: 'quotes',
        label: 'Quote Requests',
        hint: 'Customer catering quote requests',
        Icon: FaFileInvoiceDollar,
        color: '#7c3aed',
        bg: '#ede9fe',
    },
    ORDER_VIEW_OPTIONS[1],
];

/** Party Hall tab: open quotes vs confirmed bookings. */
export const PARTY_HALL_VIEW_OPTIONS = [
    {
        value: 'quotes',
        label: 'Quotes',
        hint: 'Open party hall quote requests',
        Icon: FaFileInvoiceDollar,
        color: '#7c3aed',
        bg: '#ede9fe',
    },
    {
        value: 'orders',
        label: 'Bookings',
        hint: 'Confirmed party hall bookings',
        Icon: FaClipboardList,
        color: '#1d4ed8',
        bg: '#dbeafe',
    },
];

export const ONLINE_CHANNEL_OPTIONS = [
    {
        value: 'DELIVERY',
        label: 'Delivery',
        hint: 'Orders delivered to customer',
        Icon: FaMotorcycle,
        color: '#c2410c',
        bg: '#ffedd5',
    },
    {
        value: 'PICKUP',
        label: 'Pickup',
        hint: 'Customer collects at restaurant',
        Icon: FaShoppingBag,
        color: '#0f766e',
        bg: '#ccfbf1',
    },
];

const MEAL_META = {
    '': {
        label: 'All meal types',
        hint: 'Breakfast, lunch, snacks & dinner',
        Icon: FaUtensils,
        color: '#6d28d9',
        bg: '#ede9fe',
    },
    Breakfast: { Icon: FaCoffee, color: '#b45309', bg: '#fef3c7' },
    Lunch: { Icon: FaSun, color: '#0e7490', bg: '#cffafe' },
    Snacks: { Icon: FaCookie, color: '#c2410c', bg: '#ffedd5' },
    Dinner: { Icon: FaMoon, color: '#6d28d9', bg: '#ede9fe' },
};

export const MEAL_FILTER_OPTIONS = [
    {
        value: '',
        ...MEAL_META[''],
    },
    ...MEAL_TYPE_OPTIONS.map((meal) => ({
        value: meal,
        label: meal,
        hint: `${meal} orders only`,
        ...MEAL_META[meal],
    })),
];

function RichSelectOption({ option, selected, onSelect }) {
    const { Icon, color, bg, label, hint } = option;

    return (
        <button
            type="button"
            role="option"
            aria-selected={selected}
            className={`rich-dropdown__option${selected ? ' rich-dropdown__option--selected' : ''}`}
            onClick={() => onSelect(option.value)}
        >
            <span
                className="rich-dropdown__option-icon"
                style={{ color, backgroundColor: bg }}
            >
                <Icon aria-hidden="true" />
            </span>
            <span className="rich-dropdown__option-body">
                <span className="rich-dropdown__option-label">{label}</span>
                {hint ? <span className="rich-dropdown__option-hint">{hint}</span> : null}
            </span>
            <span className="rich-dropdown__option-mark" aria-hidden="true">
                {selected ? <FaCheck /> : null}
            </span>
        </button>
    );
}

export default function ToolbarRichSelect({
    panelId,
    value,
    onChange,
    options,
    ariaLabel,
    narrow = false,
    panelTitle = 'Select option',
}) {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const [panelStyle, setPanelStyle] = useState({
        position: 'fixed',
        top: 0,
        left: 0,
        width: 240,
        zIndex: 10050,
        visibility: 'hidden',
    });

    const selected =
        options.find((opt) => opt.value === value) ||
        options.find((opt) => opt.value === String(value)) ||
        options[0];
    const SelectedIcon = selected?.Icon;

    const updatePosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const pad = 8;
        const width = Math.max(Math.round(r.width), narrow ? 200 : 240);
        let left = r.left;
        if (left + width > window.innerWidth - pad) {
            left = Math.max(pad, window.innerWidth - width - pad);
        }
        const maxPanelH = 320;
        let top = r.bottom + 4;
        if (top + maxPanelH > window.innerHeight - pad) {
            top = Math.max(pad, r.top - maxPanelH - 4);
        }
        setPanelStyle({
            position: 'fixed',
            left,
            top,
            width,
            zIndex: 10050,
            visibility: 'visible',
        });
    }, [narrow]);

    useLayoutEffect(() => {
        if (!open) return undefined;
        updatePosition();
        const onResizeOrScroll = () => updatePosition();
        window.addEventListener('resize', onResizeOrScroll);
        window.addEventListener('scroll', onResizeOrScroll, true);
        return () => {
            window.removeEventListener('resize', onResizeOrScroll);
            window.removeEventListener('scroll', onResizeOrScroll, true);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (e) => {
            const t = e.target;
            if (!(t instanceof Node)) return;
            if (triggerRef.current?.contains(t)) return;
            if (panelRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    const handleSelect = (nextValue) => {
        onChange(nextValue);
        setOpen(false);
    };

    const panel =
        open &&
        createPortal(
            <div
                ref={panelRef}
                id={panelId}
                className="jreats-order-filter__panel jreats-order-filter__panel--floating rich-dropdown__panel"
                style={panelStyle}
                role="listbox"
                aria-label={ariaLabel || panelTitle}
            >
                <div className="rich-dropdown__panel-header">
                    <span className="rich-dropdown__panel-title">{panelTitle}</span>
                    <span className="rich-dropdown__panel-count">{options.length} options</span>
                </div>
                <div className="rich-dropdown__panel-body">
                    {options.map((opt) => (
                        <RichSelectOption
                            key={opt.value || '__all__'}
                            option={opt}
                            selected={opt.value === value}
                            onSelect={handleSelect}
                        />
                    ))}
                </div>
            </div>,
            document.body
        );

    return (
        <div className={`toolbar-rich-select${narrow ? ' toolbar-rich-select--narrow' : ''}`}>
            <button
                type="button"
                ref={triggerRef}
                className={`toolbar-rich-select__trigger${open ? ' toolbar-rich-select__trigger--open' : ''}`}
                aria-expanded={open}
                aria-controls={panelId}
                aria-label={ariaLabel}
                onClick={() => setOpen((v) => !v)}
            >
                {SelectedIcon ? (
                    <span
                        className="toolbar-rich-select__trigger-icon"
                        style={{
                            color: selected.color,
                            backgroundColor: selected.bg,
                        }}
                    >
                        <SelectedIcon aria-hidden="true" />
                    </span>
                ) : null}
                <span className="toolbar-rich-select__trigger-label">{selected?.label}</span>
            </button>
            {panel}
        </div>
    );
}
